import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ThreadsLoginButton from '../components/ThreadsLoginButton';
import { useUserAuth } from '../context/UserAuthContext';
import axios from 'axios';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #ffffffff;
  padding: 40px 24px;
`;

const VerifiedToast = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(0, 210, 100, 0.12);
  border: 1px solid rgba(0, 210, 100, 0.3);
  border-radius: 10px;
  padding: 14px 18px;
  width: 100%;
  max-width: 420px;
  margin-bottom: 20px;
  font-size: 0.88rem;
  color: #4dde99;
  font-weight: 600;
  animation: ${fadeIn} 0.3s ease;
`;

const VerifyErrorToast = styled(VerifiedToast)`
  background: rgba(255, 80, 80, 0.10);
  border-color: rgba(255, 80, 80, 0.28);
  color: #ff8080;
`;

const NeedsVerifyPanel = styled.div`
  background: rgba(254, 136, 241, 0.08);
  border: 1px solid rgba(254, 136, 241, 0.2);
  border-radius: 10px;
  padding: 18px;
  text-align: center;
  margin: 12px 0 0;
  animation: ${fadeIn} 0.3s ease;
`;

const NeedsVerifyText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.88rem;
  margin: 0 0 10px;
  line-height: 1.5;
`;

const ResendBtn = styled.button`
  background: none;
  border: none;
  color: #fe88f1;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  &:hover { opacity: 0.75; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

const ResendMsg = styled.p`
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.82rem;
  margin: 8px 0 0;
`;

const Card = styled.form`
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 48px 40px;
  width: 100%;
  max-width: 420px;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  color: #fff;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 0 32px;
  letter-spacing: -0.5px;
  span { color: #fe88f1; }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.4);
  margin-bottom: 7px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255,255,255,0.05);
  border: 1.5px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  font-family: inherit;
  box-sizing: border-box;
  margin-bottom: 18px;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: rgba(254,136,241,0.5); }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const Btn = styled.button`
  width: 100%;
  padding: 13px;
  background: #fe88f1;
  color: #0d0d0d;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  &:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ErrorMsg = styled.p`
  color: #ff7e7e;
  font-size: 0.85rem;
  text-align: center;
  margin: 12px 0 0;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 420px;
  margin: 0 0 20px;
  gap: 12px;
  color: rgba(255,255,255,0.3);
  font-size: 14px;
  &::before, &::after { content: ''; flex: 1; height: 1px; background: rgba(255,255,255,0.1); }
`;

const SignUpLink = styled.p`
  color: rgba(255,255,255,0.4);
  font-size: 0.85rem;
  text-align: center;
  margin-top: 20px;
  a { color: #fe88f1; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const ForgotLink = styled.button`
  background: none;
  border: none;
  color: rgba(255,255,255,0.35);
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  display: block;
  margin: -10px 0 18px auto;
  transition: color 0.2s;
  &:hover { color: #fe88f1; }
`;

const ForgotPanel = styled.div`
  background: rgba(254,136,241,0.06);
  border: 1px solid rgba(254,136,241,0.18);
  border-radius: 10px;
  padding: 18px;
  margin-bottom: 18px;
  animation: ${fadeIn} 0.25s ease;
  transition: all 0.3s ease;
`;

const ForgotTitle = styled.p`
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  margin: 0 0 10px;
`;

const ForgotInput = styled(Input)`
  margin-bottom: 10px;
`;

const ForgotBtn = styled(Btn)`
  margin-bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ForgotSuccess = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 6px 0 2px;
  animation: ${fadeIn} 0.35s ease;
`;

const ForgotSuccessIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(77,222,153,0.15);
  border: 2px solid #4dde99;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  margin-bottom: 2px;
`;

const ForgotSuccessText = styled.p`
  color: #4dde99;
  font-size: 0.88rem;
  font-weight: 700;
  margin: 0;
  text-align: center;
`;

const ForgotSuccessSub = styled.p`
  color: rgba(255,255,255,0.4);
  font-size: 0.78rem;
  margin: 0;
  text-align: center;
  line-height: 1.4;
`;

/* Tiny inline spinner for the button */
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const BtnSpinner = styled.span`
  width: 15px;
  height: 15px;
  border: 2px solid rgba(0,0,0,0.25);
  border-top-color: #0d0d0d;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  display: inline-block;
  flex-shrink: 0;
`;

const ResetToast = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(0, 210, 100, 0.12);
  border: 1px solid rgba(0, 210, 100, 0.3);
  border-radius: 10px;
  padding: 14px 18px;
  width: 100%;
  max-width: 420px;
  margin-bottom: 20px;
  font-size: 0.88rem;
  color: #4dde99;
  font-weight: 600;
  animation: ${fadeIn} 0.3s ease;
`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const SignIn = () => {
  const { login } = useUserAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  // Forgot password state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotError, setForgotError] = useState('');

  const isVerified   = searchParams.get('verified') === '1';
  const hasVerifyErr = searchParams.get('verifyError') === '1';
  const isReset      = searchParams.get('reset') === '1';

  // Clear resend message when email changes
  useEffect(() => { setResendMsg(''); }, [email]);

  const handleForgot = useCallback(async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMsg('');
    setForgotLoading(true);
    try {
      // Use a relative URL so Apache proxies it correctly in production.
      // axios with an absolute BACKEND_URL can bypass the proxy on the built bundle.
      const res = await fetch('/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setForgotMsg('Reset link sent! Check your inbox.');
    } catch (err) {
      setForgotError(err.message || 'Could not send reset email. Try again.');
    } finally {
      setForgotLoading(false);
    }
  }, [forgotEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerify(false);
    setLoading(true);
    try {
      await login(email, password);
      navigate('/my-films');
    } catch (err) {
      if (err.needsVerification) {
        setNeedsVerify(true);
      } else {
        setError(err.message || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setResendMsg('Enter your email above first.');
      return;
    }
    setResendLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/resend-verification`, { email });
      setResendMsg('Verification email sent! Check your inbox.');
    } catch (err) {
      setResendMsg(err.response?.data?.error || 'Could not resend. Try again later.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Container>
      <Helmet>
        <title>Sign In — MyCinemaDB</title>
        <meta name="description" content="Sign in to your MyCinemaDB account to save your favorite movies and access personalized film recommendations." />
      </Helmet>

      {isVerified && (
        <VerifiedToast>
          <span>✅</span>
          Email verified! You can now sign in below.
        </VerifiedToast>
      )}

      {hasVerifyErr && (
        <VerifyErrorToast>
          <span>⚠️</span>
          Verification link is invalid or already used. Request a new one below.
        </VerifyErrorToast>
      )}

      {isReset && (
        <ResetToast>
          <span>✅</span>
          Password updated! You can now sign in with your new password.
        </ResetToast>
      )}

      <Card onSubmit={handleSubmit}>
        <Title>My<span>Cinema</span>DB</Title>
        <FieldLabel htmlFor="si-email">Email</FieldLabel>
        <Input id="si-email" type="email" placeholder="you@email.com" value={email}
          onChange={(e) => { setEmail(e.target.value); setNeedsVerify(false); setError(''); }}
          required autoComplete="email" />
        <FieldLabel htmlFor="si-password">Password</FieldLabel>
        <Input id="si-password" type="password" placeholder="••••••••" value={password}
          onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />

        {/* Forgot password link */}
        <ForgotLink type="button" onClick={() => { setForgotOpen((o) => !o); setForgotMsg(''); setForgotError(''); }}>
          Forgot password?
        </ForgotLink>

        {/* Inline forgot-password panel */}
        {forgotOpen && (
          <ForgotPanel>
            <ForgotTitle>📧 Reset your password</ForgotTitle>
            {forgotMsg ? (
              <ForgotSuccess>
                <ForgotSuccessIcon>✓</ForgotSuccessIcon>
                <ForgotSuccessText>Email sent!</ForgotSuccessText>
                <ForgotSuccessSub>
                  Check your inbox for <strong style={{ color: '#fff' }}>{forgotEmail}</strong>.<br />
                  The link expires in 1 hour.
                </ForgotSuccessSub>
              </ForgotSuccess>
            ) : (
              <form onSubmit={handleForgot}>
                {forgotError && <p style={{ color: '#ff7e7e', fontSize: '0.82rem', margin: '0 0 8px' }}>{forgotError}</p>}
                <ForgotInput
                  type="email"
                  placeholder="your@email.com"
                  value={forgotEmail}
                  onChange={(e) => { setForgotEmail(e.target.value); setForgotError(''); }}
                  required
                  autoFocus
                />
                <ForgotBtn type="submit" disabled={forgotLoading}>
                  {forgotLoading && <BtnSpinner />}
                  {forgotLoading ? 'Sending…' : 'Send Reset Link'}
                </ForgotBtn>
              </form>
            )}
          </ForgotPanel>
        )}

        <Btn type="submit" disabled={loading}>{loading ? 'Signing in…' : 'Sign In'}</Btn>
        {error && <ErrorMsg>{error}</ErrorMsg>}

        {needsVerify && (
          <NeedsVerifyPanel>
            <NeedsVerifyText>
              📬 Your email isn't verified yet. Check your inbox for the verification link.
            </NeedsVerifyText>
            <ResendBtn onClick={handleResend} type="button" disabled={resendLoading}>
              {resendLoading ? 'Sending…' : 'Resend verification email'}
            </ResendBtn>
            {resendMsg && <ResendMsg>{resendMsg}</ResendMsg>}
          </NeedsVerifyPanel>
        )}

        <SignUpLink>Don't have an account? <Link to="/sign-up">Sign Up</Link></SignUpLink>
      </Card>

      <Divider>or continue with</Divider>
      <ThreadsLoginButton returnTo="/my-films" />
    </Container>
  );
};

export default SignIn;